import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { AppBadge, AppButton } from "../../../shared/components/ui";
import { useFieldHeader, useFieldJourney } from "../../../shared/components/FieldShell";
import {
  IconCheck,
  IconError,
  IconInfo,
  IconSpinner,
  IconWarning,
} from "../../../shared/components/icons";
import { getPayment } from "../api";
import { formatDate, formatTzs } from "../format";
import type { Payment, PaymentAllocation } from "../types";

/**
 * S3 — Allocation & activation receipt (handoff TASK-009, s3-allocation-*).
 * Arrears-first allocation, transactional server-side: received → arrears
 * first → remaining → activation days (floor at the plan-tier rate;
 * remainder kept as credit) → new paid-through date.
 * Edge: arrears-only payments buy 0 days — no token is issued and the
 * remaining arrears balance is shown as still open.
 * Error variant: ledger conflict — the fetch is idempotent, Retry is safe.
 */

export function S3AllocationReceipt() {
  const { id = "" } = useParams();
  const paymentId = Number(id);
  const navigate = useNavigate();

  const query = useQuery({
    queryKey: ["payment", paymentId],
    queryFn: () => getPayment(paymentId),
    retry: 1,
    // Poll while a verified payment is still allocating.
    refetchInterval: (q) => {
      const p = q.state.data?.payment;
      return p && p.status === "verified" && !p.allocation ? 3_000 : false;
    },
  });

  const payment: Payment | undefined = query.data?.payment;
  useFieldHeader(
    "Allocation & activation",
    payment
      ? `${payment.customer_name ?? "Customer"} — ${payment.contract_number ?? payment.payment_number}`
      : undefined,
  );
  useFieldJourney({ flow: "payments", step: 2, backTo: `/field/payments/${id}/verification` });

  if (query.isPending) {
    return (
      <div className="pg-state">
        <span className="state-icon info">
          <IconSpinner size={40} />
        </span>
        <h2>Allocating payment…</h2>
      </div>
    );
  }

  if (query.isError) {
    return (
      <div className="pg-state">
        <span className="state-icon error">
          <IconError size={40} />
        </span>
        <h2>Ledger conflict</h2>
        <p>
          The allocation could not be read from the ledger. The allocation is transactional and
          idempotent — retrying is safe and can never double-apply.
        </p>
        <AppButton variant="secondary" type="button" onClick={() => void query.refetch()}>
          Retry
        </AppButton>
      </div>
    );
  }

  if (!payment || !payment.allocation) {
    // Verified but allocation still running (or nothing to allocate).
    return (
      <div className="pg-state">
        <span className="state-icon info">
          <IconSpinner size={40} />
        </span>
        <h2>Allocating payment…</h2>
        <p>Arrears settle first, then activation days. This screen updates automatically.</p>
      </div>
    );
  }

  const a: PaymentAllocation = payment.allocation;
  const arrearsOnly = a.activation_days === 0 && a.arrears_applied_cents > 0 && !(a.shortfall_cents && a.shortfall_cents > 0);
  const shortOfToken = (a.shortfall_cents ?? 0) > 0 && a.activation_days === 0 && !a.fully_paid;
  const tokenExpected = ((a.activation_days > 0 && a.token_eligible !== false) || a.fully_paid) && payment.id !== null;
  // F8 (QC TASK-QC-002): the wire `remaining_cents` currently carries the
  // CONTRACT's remaining principal (step-06: TZS 160,000), not this payment's
  // remainder after arrears — the design-receipt semantic (40,000 − 25,000 =
  // 15,000), which is also the only value consistent with the receipt's own
  // days × rate + credit. Derive the row from exact wire values; the result
  // is identical once the backend fixes the wire semantics.
  const paymentRemainingCents = Math.max(0, a.amount_cents - a.arrears_applied_cents);

  return (
    <>
      <div className="pg-summary" aria-label="Allocation receipt">
        <div className="row">
          <span className="k">Amount received</span>
          <span className="v">{formatTzs(a.amount_cents)}</span>
        </div>
        <div className="row">
          <span className="k">Arrears applied (first)</span>
          <span className="v warn">-{formatTzs(a.arrears_applied_cents)}</span>
        </div>
        <div className="row">
          <span className="k">Remaining</span>
          <span className="v">{formatTzs(paymentRemainingCents)}</span>
        </div>
        <div className="row">
          <span className="k">Activation days purchased</span>
          <span className="v">{a.activation_days} days</span>
        </div>
        <div className="row">
          <span className="k">Rate</span>
          <span className="v">{formatTzs(a.rate_per_day_cents)} / day</span>
        </div>
        {a.credit_cents > 0 ? (
          <div className="row">
            <span className="k">Credit kept (sub-day remainder)</span>
            <span className="v">{formatTzs(a.credit_cents)}</span>
          </div>
        ) : null}
        {(a.installment_credit_after_cents ?? 0) > 0 || (a.installment_min_cents ?? 0) > 0 ? (
          <div className="row">
            <span className="k">Toward next installment token</span>
            <span className="v">
              {formatTzs(a.installment_credit_after_cents ?? 0)}
              {(a.installment_min_cents ?? 0) > 0
                ? ` / ${formatTzs(a.installment_min_cents ?? 0)}`
                : ""}
            </span>
          </div>
        ) : null}
        {(a.shortfall_cents ?? 0) > 0 ? (
          <div className="row">
            <span className="k">Still needed for a token</span>
            <span className="v warn">{formatTzs(a.shortfall_cents ?? 0)}</span>
          </div>
        ) : null}
        <div className="row">
          <span className="k">New paid-through date</span>
          <span className="v good">{a.new_paid_through_date ? formatDate(a.new_paid_through_date) : "—"}</span>
        </div>
      </div>

      {a.arrears_applied_cents > 0 && a.arrears_balance_cents === 0 ? (
        <div className="pg-verify-summary">
          <AppBadge tone="success">
            <IconCheck size={12} /> Arrears cleared
          </AppBadge>
          <span className="vs-meta">Arrears balance: {formatTzs(0)}</span>
        </div>
      ) : null}

      {arrearsOnly ? (
        <div className="pg-idem-note">
          <IconInfo size={14} /> Arrears only — no activation days purchased. Arrears balance still
          open: {formatTzs(a.arrears_balance_cents)}. No token is issued for this payment.
        </div>
      ) : null}

      {shortOfToken ? (
        <div className="pg-idem-note">
          <IconInfo size={14} /> Payment reduces the loan, but is below the installment minimum for
          a token. Pay {formatTzs(a.shortfall_cents ?? 0)} more to unlock a token.
        </div>
      ) : null}

      {a.fully_paid ? (
        <div className="pg-verify-summary">
          <AppBadge tone="success">
            <IconCheck size={12} /> Fully paid
          </AppBadge>
          <span className="vs-meta">Permanent unlock will be issued.</span>
        </div>
      ) : null}

      {payment.status !== "verified" ? (
        <div className="pg-inline-error" role="alert">
          <IconWarning size={14} /> This payment is {payment.status} — the allocation shown is from
          the original verified payment.
        </div>
      ) : null}

      <div className="pg-actions">
        {tokenExpected ? (
          <AppButton type="button" onClick={() => navigate(`/field/payments/${payment.id}/token`)}>
            Continue to token
          </AppButton>
        ) : (
          <AppButton
            type="button"
            onClick={() => navigate(`/field/customers/${payment.customer_id}/payment-timeline`)}
          >
            Done — view timeline
          </AppButton>
        )}
      </div>
    </>
  );
}
